import type { Id } from "../../../convex/_generated/dataModel";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type {
  ManagedTag,
  TagDraft,
  TagFormSubmitHandler,
} from "./settings-types";

type EditTagDialogProps = {
  tag: ManagedTag | null;
  draft: TagDraft;
  formError: string | null;
  pendingTagAction: Id<"tag"> | "new" | null;
  onDraftChange: (draft: TagDraft) => void;
  onSubmit: TagFormSubmitHandler;
  onCancel: () => void;
};

export function EditTagDialog({
  tag,
  draft,
  formError,
  pendingTagAction,
  onDraftChange,
  onSubmit,
  onCancel,
}: EditTagDialogProps) {
  if (tag === null) {
    return null;
  }

  const isSaving = pendingTagAction === tag._id;

  return (
    <Dialog
      open={true}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          onCancel();
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit property tag</DialogTitle>
          <DialogDescription>
            Update how agents see this tag in property forms and filters.
          </DialogDescription>
        </DialogHeader>
        <form className="space-y-4" onSubmit={onSubmit}>
          <div>
            <Label htmlFor="edit-property-tag-name">Tag name</Label>
            <Input
              id="edit-property-tag-name"
              value={draft.tagName}
              onChange={(event) =>
                onDraftChange({
                  ...draft,
                  tagName: event.target.value,
                })
              }
              placeholder="Beachfront"
              disabled={isSaving}
              className="mt-2"
            />
          </div>
          <div>
            <Label htmlFor="edit-property-tag-name-idn">Indonesian label</Label>
            <Input
              id="edit-property-tag-name-idn"
              value={draft.tagNameIdn}
              onChange={(event) =>
                onDraftChange({
                  ...draft,
                  tagNameIdn: event.target.value,
                })
              }
              placeholder="Tepi pantai"
              disabled={isSaving}
              className="mt-2"
            />
          </div>
          {formError !== null ? (
            <p className="text-sm text-destructive">{formError}</p>
          ) : null}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={isSaving}
            >
              Keep current tag
            </Button>
            <Button type="submit" disabled={isSaving}>
              {isSaving ? "Saving tag" : "Save tag"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

type ArchiveTagDialogProps = {
  tag: ManagedTag | null;
  formError: string | null;
  pendingTagAction: Id<"tag"> | "new" | null;
  onCancel: () => void;
  onArchive: (tag: ManagedTag) => void;
};

export function ArchiveTagDialog({
  tag,
  formError,
  pendingTagAction,
  onCancel,
  onArchive,
}: ArchiveTagDialogProps) {
  if (tag === null) {
    return null;
  }

  const isArchiving = pendingTagAction === tag._id;

  return (
    <Dialog
      open={true}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          onCancel();
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Archive property tag</DialogTitle>
          <DialogDescription>
            Archive "{tag.tag_name}"? It will disappear from active tag lists,
            while existing properties keep the reference.
          </DialogDescription>
        </DialogHeader>
        {formError !== null ? (
          <p className="text-sm text-destructive">{formError}</p>
        ) : null}
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isArchiving}
          >
            Keep tag
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={() => onArchive(tag)}
            disabled={isArchiving}
          >
            {isArchiving ? "Archiving tag" : "Archive this tag"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
